import { plural, t } from '@lingui/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';

import { HeaderTitlesEnum, LastMinuteHeaderTitle } from './constants';

/**
 * Service types this builder produces copy for. Walk and drop-in get pluralized
 * strings; the stay-based services (boarding, traveling, daycare) share the
 * non-pluralized "stay" copy. Services outside this set (e.g. grooming, training)
 * are intentionally unsupported and excluded from the accepted type, so callers
 * must resolve a supported service type before building a title.
 */
const SUPPORTED_SERVICE_TYPES = [
  ServiceType.OVERNIGHT_BOARDING,
  ServiceType.OVERNIGHT_TRAVELING,
  ServiceType.DOGGY_DAY_CARE,
  ServiceType.DOG_WALKING,
  ServiceType.DROP_IN,
] as const;

export type LastMinuteRequestServiceType = (typeof SUPPORTED_SERVICE_TYPES)[number];

export const isSupportedServiceType = (
  serviceType?: ServiceType
): serviceType is LastMinuteRequestServiceType =>
  !!serviceType && (SUPPORTED_SERVICE_TYPES as readonly ServiceType[]).includes(serviceType);

const isWalkingService = (serviceType: LastMinuteRequestServiceType): boolean =>
  serviceType === ServiceType.DOG_WALKING;

const isDropInService = (serviceType: LastMinuteRequestServiceType): boolean =>
  serviceType === ServiceType.DROP_IN;

const buildSingleDayWalkingServiceHeader = (
  scenario: LastMinuteHeaderTitle,
  unitCount: number,
  dayOfWeek: string
): string => {
  switch (scenario) {
    case HeaderTitlesEnum.LAST_MINUTE_SAME_DAY:
      return plural(unitCount, {
        one: `Book your walk for today`,
        other: `Book your walks for today`,
      });
    case HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY:
      return plural(unitCount, {
        one: `Book your walk for tomorrow`,
        other: `Book your walks for tomorrow`,
      });
    case HeaderTitlesEnum.LAST_MINUTE_TWO_DAY:
    default:
      return plural(unitCount, {
        one: `Book your walk for ${dayOfWeek}`,
        other: `Book your walks for ${dayOfWeek}`,
      });
  }
};

const buildMultiDayWalkingServiceHeader = (
  scenario: LastMinuteHeaderTitle,
  dayOfWeek: string
): string => {
  switch (scenario) {
    case HeaderTitlesEnum.LAST_MINUTE_SAME_DAY:
      return t`Book your walks starting today`;
    case HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY:
      return t`Book your walks starting tomorrow`;
    case HeaderTitlesEnum.LAST_MINUTE_TWO_DAY:
    default:
      return t`Book your walks starting ${dayOfWeek}`;
  }
};

const buildSingleDayDropInServiceHeader = (
  scenario: LastMinuteHeaderTitle,
  unitCount: number,
  dayOfWeek: string
): string => {
  switch (scenario) {
    case HeaderTitlesEnum.LAST_MINUTE_SAME_DAY:
      return plural(unitCount, {
        one: `Book your visit for today`,
        other: `Book your visits for today`,
      });
    case HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY:
      return plural(unitCount, {
        one: `Book your visit for tomorrow`,
        other: `Book your visits for tomorrow`,
      });
    case HeaderTitlesEnum.LAST_MINUTE_TWO_DAY:
    default:
      return plural(unitCount, {
        one: `Book your visit for ${dayOfWeek}`,
        other: `Book your visits for ${dayOfWeek}`,
      });
  }
};

const buildMultiDayDropInServiceHeader = (
  scenario: LastMinuteHeaderTitle,
  dayOfWeek: string
): string => {
  switch (scenario) {
    case HeaderTitlesEnum.LAST_MINUTE_SAME_DAY:
      return t`Book your visits starting today`;
    case HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY:
      return t`Book your visits starting tomorrow`;
    case HeaderTitlesEnum.LAST_MINUTE_TWO_DAY:
    default:
      return t`Book your visits starting ${dayOfWeek}`;
  }
};

// Stay-based services: boarding, house sitting (traveling), and daycare. These do
// not pluralize, so the unit count is irrelevant.
const buildSingleDayStayServiceHeader = (
  scenario: LastMinuteHeaderTitle,
  dayOfWeek: string
): string => {
  switch (scenario) {
    case HeaderTitlesEnum.LAST_MINUTE_SAME_DAY:
      return t`Book your stay for today`;
    case HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY:
      return t`Book your stay for tomorrow`;
    case HeaderTitlesEnum.LAST_MINUTE_TWO_DAY:
    default:
      return t`Book your stay for ${dayOfWeek}`;
  }
};

const buildMultiDayStayServiceHeader = (
  scenario: LastMinuteHeaderTitle,
  dayOfWeek: string
): string => {
  switch (scenario) {
    case HeaderTitlesEnum.LAST_MINUTE_SAME_DAY:
      return t`Book your stay starting today`;
    case HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY:
      return t`Book your stay starting tomorrow`;
    case HeaderTitlesEnum.LAST_MINUTE_TWO_DAY:
    default:
      return t`Book your stay starting ${dayOfWeek}`;
  }
};

const buildRecurringStayServiceHeader = (
  scenario: LastMinuteHeaderTitle,
  dayOfWeek: string
): string => {
  switch (scenario) {
    case HeaderTitlesEnum.LAST_MINUTE_SAME_DAY:
      return t`Book your stays starting today`;
    case HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY:
      return t`Book your stays starting tomorrow`;
    case HeaderTitlesEnum.LAST_MINUTE_TWO_DAY:
    default:
      return t`Book your stays starting ${dayOfWeek}`;
  }
};

export type BuildLastMinuteRequestTitleParams = {
  /** The last-minute lead-time scenario being rendered. */
  scenario: LastMinuteHeaderTitle;
  /**
   * The service type being searched. Restricted to services this builder supports
   * (see LastMinuteRequestServiceType).
   */
  serviceType: LastMinuteRequestServiceType;
  /**
   * True when the search covers a single calendar day (one-time, start === end).
   * Single-day searches read "...for {when}"; multi-day or recurring read "...starting {when}".
   */
  isSingleDay: boolean;
  /**
   * Number of service units booked on a single day (e.g. 2 walks). Only affects
   * walk/drop-in pluralization for single-day searches; defaults to 1.
   */
  unitCount?: number;
  /** Localized day of week, required only for the `LAST_MINUTE_TWO_DAY` scenario. */
  dayOfWeek?: string;
  isRecurring?: boolean;
};

/**
 * Resolves the localized last-minute request title sentence for a search section.
 * Delegates to a per-service builder that selects a fully-formed sentence for the
 * scenario (today / tomorrow / a localized day of week) and date span.
 */
const buildLastMinuteRequestTitle = ({
  scenario,
  serviceType,
  isSingleDay,
  unitCount = 1,
  dayOfWeek = '',
  isRecurring = false,
}: BuildLastMinuteRequestTitleParams): string => {
  if (isWalkingService(serviceType)) {
    return isSingleDay
      ? buildSingleDayWalkingServiceHeader(scenario, unitCount, dayOfWeek)
      : buildMultiDayWalkingServiceHeader(scenario, dayOfWeek);
  }
  if (isDropInService(serviceType)) {
    return isSingleDay
      ? buildSingleDayDropInServiceHeader(scenario, unitCount, dayOfWeek)
      : buildMultiDayDropInServiceHeader(scenario, dayOfWeek);
  }
  if (isRecurring) {
    return buildRecurringStayServiceHeader(scenario, dayOfWeek);
  }
  return isSingleDay
    ? buildSingleDayStayServiceHeader(scenario, dayOfWeek)
    : buildMultiDayStayServiceHeader(scenario, dayOfWeek);
};

export default buildLastMinuteRequestTitle;
